import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    input,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { qpxToast as toast } from '@quipex/core/utils';
import {
    QpxFileTypeIcon,
    QpxButton,
    injectFilePreviewDialog,
} from '@quipex/shared/ui';
import { finalize } from 'rxjs';
import {
    isCanonicalDocumentId,
    QuippyApi,
    type QuippyAssistantAppearance,
    type QuippySource,
} from '../../../runtime';
import { QuippySourceHover } from './source-document-hover';
import { toQuippySourceFileName } from './source-file-name';

@Component({
    selector: 'qpx-quippy-related-documents',
    imports: [QpxFileTypeIcon, MatButtonModule, QuippySourceHover, QpxButton],
    templateUrl: './related-documents.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex flex-wrap items-center gap-1.5',
        role: 'group',
        'aria-label': 'Source documents',
    },
})
export class QuippyRelatedDocuments {
    //#region Dependency injection
    private readonly api = inject(QuippyApi);
    private readonly destroyRef = inject(DestroyRef);
    private readonly filePreviewDialog = injectFilePreviewDialog();
    //#endregion

    //#region Inputs
    public readonly sourceSetId = input('');
    public readonly sources = input.required<readonly QuippySource[]>();
    public readonly appearance = input<QuippyAssistantAppearance>('elevated');
    public readonly sourceFallbackBuildingId = input<string | null>(null);
    //#endregion

    //#region State
    protected readonly pendingSourceId = signal<string | null>(null);
    //#endregion

    //#region Template methods
    protected openSourcePreview(source: QuippySource): void {
        const buildingId = (
            source.buildingId ?? this.sourceFallbackBuildingId()
        )?.trim();

        if (!buildingId || !isCanonicalDocumentId(source.id)) {
            toast.info('Document preview is not available for this source.');
            return;
        }

        if (this.pendingSourceId() === source.id) return;

        this.pendingSourceId.set(source.id);

        this.api
            .getDocumentViewUrl(buildingId, source.id)
            .pipe(
                finalize(() => {
                    if (this.pendingSourceId() === source.id) {
                        this.pendingSourceId.set(null);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (target) => {
                    if (this.pendingSourceId() !== source.id) return;

                    this.filePreviewDialog.open({
                        previewUrl: target.viewUrl,
                        fileName: target.fileName,
                    });
                },
                error: () => {
                    if (this.pendingSourceId() !== source.id) return;

                    // eslint-disable-next-line agentic/prefer-auto-error-toast -- source preview intentionally suppresses HTTP auto-toast so inaccessible/private documents get one generic local message.
                    toast.error(
                        'Failed to open the document preview. Please try again.'
                    );
                },
            });
    }

    protected isSourcePending(source: QuippySource): boolean {
        return (
            isCanonicalDocumentId(source.id) &&
            this.pendingSourceId() === source.id
        );
    }

    protected isSourcePreviewAvailable(source: QuippySource): boolean {
        const buildingId = (
            source.buildingId ?? this.sourceFallbackBuildingId()
        )?.trim();

        return Boolean(buildingId) && isCanonicalDocumentId(source.id);
    }

    protected sourceFileName(source: QuippySource) {
        return toQuippySourceFileName(source);
    }

    protected sourceBuildingName(source: QuippySource): string | null {
        return this.sourceFallbackBuildingId()
            ? null
            : source.buildingName?.trim() || null;
    }

    protected sourceLabel(source: QuippySource): string {
        const buildingName = this.sourceBuildingName(source);
        return buildingName
            ? `${source.title} - ${buildingName}`
            : source.title;
    }
    //#endregion
}
